import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
    getMatchedTestResultByIdError,
    getMatchedTestResultByIdRequest,
    getMatchedTestResultByIdSuccess,
    getMatchedTestResultsError,
    getMatchedTestResultsRequest,
    getMatchedTestResultsSuccess,
    getTestResultByIdError,
    getTestResultByIdRequest,
    getTestResultByIdSuccess,
    getTestResultsBranchesFilterError,
    getTestResultsBranchesFilterRequest,
    getTestResultsBranchesFilterSuccess,
    getTestResultsBuildsFilterError,
    getTestResultsBuildsFilterRequest,
    getTestResultsBuildsFilterSuccess,
    getTestResultsByTestcaseError,
    getTestResultsByTestcaseRequest,
    getTestResultsByTestcaseSuccess,
    getTestResultsError,
    getTestResultsRequest,
    getTestResultsSuccess,
    getTestResultsTimelineError,
    getTestResultsTimelineRequest,
    getTestResultsTimelineSuccess,
    getTestRunByIdError,
    getTestRunByIdRequest,
    getTestRunByIdSuccess,
    getTestRunsBranchesFilterError,
    getTestRunsBranchesFilterRequest,
    getTestRunsBranchesFilterSuccess,
    getTestRunsBuildsFilterError,
    getTestRunsBuildsFilterRequest,
    getTestRunsBuildsFilterSuccess,
    getTestRunsError,
    getTestRunsRequest,
    getTestRunsSuccess,
    getTestRunsTimelineError,
    getTestRunsTimelineRequest,
    getTestRunsTimelineSuccess,
    patchTestResultError,
    patchTestResultRequest,
    patchTestResultSuccess,
    patchTestRunError,
    patchTestRunRequest,
    patchTestRunResultsError,
    patchTestRunResultsRequest,
    patchTestRunResultsSuccess,
    patchTestRunSuccess,
} from './index';
import {
    getTestResultBranchesFilter,
    getTestResultBuildsFilter,
    getTestResultById,
    getTestResults,
    getTestResultsByTestcase,
    getTestRunById,
    getTestRuns,
    getTestRunsBranchesFilter,
    getTestRunsBuildsFilter,
    patchTestRun,
    patchTestRunResult,
} from '../api/testrunsApi';
import { selectTestResult } from './selectors';

export function* getTestRunsSaga(params) {
    try {
        const response = yield call(getTestRuns, params.payload);
        yield put(getTestRunsSuccess(response));
    } catch (error) {
        yield put(getTestRunsError(error));
    }
}

export function* getTestResultsSaga({ payload }) {
    try {
        const response = yield call(getTestResults, payload);
        yield put(getTestResultsSuccess(response));
    } catch (error) {
        yield put(getTestResultsError(error));
    }
}

export function* getTestRunsTimelineSaga(params) {
    try {
        const response = yield call(getTestRuns, params.payload);
        yield put(getTestRunsTimelineSuccess(response));
    } catch (error) {
        yield put(getTestRunsTimelineError(error));
    }
}

export function* getTestRunByIdSaga(params) {
    try {
        let response = yield call(getTestRunById, params.payload);
        yield put(getTestRunByIdSuccess(response));
    } catch (error) {
        yield put(getTestRunByIdError(error));
    }
}

export function* patchTestRunSaga({ payload }) {
    const { data, testrunId, page_index, page_size } = payload;
    try {
        const response = yield call(patchTestRun, testrunId, data);
        yield put(patchTestRunSuccess());
        yield put(getTestRunByIdSuccess(response));
        yield put(getTestResultsRequest({ testrun: testrunId, latest_only: true, page_index, page_size }));
    } catch (error) {
        yield put(patchTestRunError(error));
    }
}

export function* patchTestRunResultsSaga({ payload }) {
    const { results, data } = payload;

    try {
        yield put(
            patchTestRunResultsSuccess({
                items: results.map((r) => ({
                    ...r,
                    ...data,
                })),
            }),
        );

        yield all(results.map((result) => call(patchTestRunResult, result.pk, data, result.testcase?.is_handtest)));

        const autotests = [];
        const handtests = [];
        results.forEach((result) => {
            if (result.testcase?.is_handtest) handtests.push(result.pk);
            else autotests.push(result.pk);
        });

        yield put(getTestResultsByTestcaseRequest({ autotests, handtests }));
    } catch (error) {
        yield put(patchTestRunResultsError(error));
    }
}

export function* patchTestResultSaga(params) {
    const { testresultId, data, selectedTestresult, createdAtLast } = params.payload;
    const testresult = yield select(selectTestResult);
    const transformedId = String(testresultId).split('-')[1] || testresultId;
    const finalId = transformedId || selectedTestresult?.pk;
    const isHandtest = testresult?.testcase?.is_handtest || selectedTestresult?.testcase?.is_handtest || false;

    try {
        yield call(patchTestRunResult, finalId, data, isHandtest);
        yield put(patchTestResultSuccess());
        yield put(
            getTestResultByIdRequest({
                testresultId: finalId,
                isHandtest: isHandtest,
            }),
        );

        if (createdAtLast && selectedTestresult?.testcase?.pk) {
            yield put(
                getTestResultsRequest({
                    createdAtLast,
                    testcase: selectedTestresult.testcase.pk,
                }),
            );
        }

        if (testresult?.testrun) {
            yield put(
                getTestResultsTimelineRequest({
                    testrun: testresult.testrun,
                }),
            );
        }
    } catch (error) {
        yield put(patchTestResultError(error));
    }
}

export function* getTestResultByIdSaga(params) {
    const { testresultId, isHandtest } = params.payload;

    try {
        const response = yield call(getTestResultById, testresultId, isHandtest);
        yield put(getTestResultByIdSuccess(response));
    } catch (error) {
        yield put(getTestResultByIdError(error));
    }
}

export function* getTestResultsTimelineSaga(params) {
    try {
        const response = yield call(getTestResults, params.payload);
        yield put(getTestResultsTimelineSuccess(response));
    } catch (error) {
        yield put(getTestResultsTimelineError(error));
    }
}

export function* getTestResultsByTestcaseSaga(params) {
    try {
        const response = yield call(getTestResultsByTestcase, params.payload);
        yield put(getTestResultsByTestcaseSuccess(response));
    } catch (error) {
        yield put(getTestResultsByTestcaseError(error));
    }
}

function* getTestResultsBuildsFilterSaga({ payload }) {
    try {
        const response = yield call(getTestResultBuildsFilter, payload);
        yield put(getTestResultsBuildsFilterSuccess(response));
    } catch (err) {
        yield put(getTestResultsBuildsFilterError(err));
    }
}

function* getTestResultsBranchesFilterSaga({ payload }) {
    try {
        const response = yield call(getTestResultBranchesFilter, payload);
        yield put(getTestResultsBranchesFilterSuccess(response));
    } catch (err) {
        yield put(getTestResultsBranchesFilterError(err));
    }
}

export function* getMatchedTestResultsSaga(action) {
    try {
        const response = yield call(getTestResults, action.payload);
        yield put(getMatchedTestResultsSuccess(response));
    } catch (error) {
        yield put(getMatchedTestResultsError(error));
    }
}

export function* getMatchedTestResultByIdSaga(action) {
    const { testresultId, isHandtest } = action.payload;

    try {
        const response = yield call(getTestResultById, testresultId, isHandtest);
        yield put(getMatchedTestResultByIdSuccess(response));
    } catch (error) {
        yield put(getMatchedTestResultByIdError(error));
    }
}

function* getTestRunsBuildsFilterSaga({ payload }) {
    try {
        const response = yield call(getTestRunsBuildsFilter, payload);
        yield put(getTestRunsBuildsFilterSuccess(response));
    } catch (err) {
        yield put(getTestRunsBuildsFilterError(err));
    }
}

function* getTestRunsBranchesFilterSaga({ payload }) {
    try {
        const response = yield call(getTestRunsBranchesFilter, payload);
        yield put(getTestRunsBranchesFilterSuccess(response));
    } catch (err) {
        yield put(getTestRunsBranchesFilterError(err));
    }
}

export default function* testRunsSagaWatcher() {
    yield all([
        takeLatest(getTestRunsRequest, getTestRunsSaga),
        takeLatest(getTestResultsRequest, getTestResultsSaga),
        takeLatest(getTestRunsTimelineRequest, getTestRunsTimelineSaga),
        takeLatest(getTestRunByIdRequest, getTestRunByIdSaga),
        takeLatest(patchTestRunRequest, patchTestRunSaga),
        takeLatest(patchTestRunResultsRequest, patchTestRunResultsSaga),
        takeLatest(patchTestResultRequest, patchTestResultSaga),
        takeLatest(getTestResultByIdRequest, getTestResultByIdSaga),
        takeLatest(getTestResultsTimelineRequest, getTestResultsTimelineSaga),
        takeLatest(getTestResultsBuildsFilterRequest, getTestResultsBuildsFilterSaga),
        takeLatest(getTestResultsBranchesFilterRequest, getTestResultsBranchesFilterSaga),
        takeLatest(getTestRunsBranchesFilterRequest, getTestRunsBranchesFilterSaga),
        takeLatest(getTestRunsBuildsFilterRequest, getTestRunsBuildsFilterSaga),
        takeLatest(getTestResultsByTestcaseRequest, getTestResultsByTestcaseSaga),
        takeLatest(getMatchedTestResultsRequest, getMatchedTestResultsSaga),
        takeLatest(getMatchedTestResultByIdRequest, getMatchedTestResultByIdSaga),
    ]);
}
